import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from '@/components/ui/button';
import { DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import '@/components/ui/styles/customScrollBar.css';
import {
   Tooltip,
   TooltipContent,
   TooltipProvider,
   TooltipTrigger,
} from "@/components/ui/tooltip";
import { AlertCircle, CheckCircle2, Download, Eye } from 'lucide-react';
import React from 'react';

const PLTPDetailsRenderer = ({ data, handlePreview, handleDownload, documentsOnRight }) => {
   const getBooleanDisplay = (value) => {
      const boolValue = Boolean(value);
      return boolValue ? 'Yes' : 'No';
   };

   const baseRequiredDocuments = {
      applicationLetter: "Application Letter",
      lguEndorsement: "LGU Endorsement",
      titleCertificate: "Title Certificate"
   };

   const conditionalDocuments = {
      darCertificate: {
         label: "DAR Certificate",
         required: data.isCLOA
      },
      specialPowerOfAttorney: {
         label: "Special Power of Attorney",
         required: data.isRepresentative
      },
      ptaResolution: {
         label: "PTA Resolution",
         required: data.isSchoolOrOrganization
      }
   };

   const getAllRequiredDocuments = () => {
      if (data.requestType === 'Inventory') {
         return {
            letterOfIntent: "Letter of Intent"
         };
      }

      const required = { ...baseRequiredDocuments };

      Object.entries(conditionalDocuments).forEach(([key, doc]) => {
         if (doc.required) {
            required[key] = doc.label;
         }
      });

      return required;
   };

   const missingDocuments = Object.entries(getAllRequiredDocuments())
      .filter(([key]) => !data.files?.[key] || data.files[key].length === 0);

   const isDocumentationComplete = missingDocuments.length === 0;

   const renderFileSection = (files, title) => {
      if (!files || !Array.isArray(files) || files.length === 0) return null;

      return (
         <div className="space-y-2">
            <h4 className="font-medium text-sm text-gray-700 dark:text-gray-300">{title}</h4>
            <div className="grid grid-cols-1 gap-2">
               {files.map((file, index) => (
                  <div
                     key={index}
                     className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 p-3 rounded-lg"
                  >
                     <span className="text-sm text-gray-600 dark:text-gray-300">{file.filename}</span>
                     <div className="flex gap-2">
                        <TooltipProvider delayDuration={250}>
                           <Tooltip>
                              <TooltipTrigger asChild>
                                 <Button
                                    size="icon"
                                    variant="outline"
                                    className="dark:border-gray-600 dark:hover:bg-gray-700"
                                    onClick={() => handlePreview(file)}
                                 >
                                    <Eye className="h-4 w-4" />
                                 </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                 <p>Preview</p>
                              </TooltipContent>
                           </Tooltip>
                        </TooltipProvider>
                        <TooltipProvider delayDuration={250}>
                           <Tooltip>
                              <TooltipTrigger asChild>
                                 <Button
                                    size="icon"
                                    variant="outline"
                                    className="dark:border-gray-600 dark:hover:bg-gray-700"
                                    onClick={() => handleDownload(file)}
                                 >
                                    <Download className="h-4 w-4" />
                                 </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                 <p>Download</p>
                              </TooltipContent>
                           </Tooltip>
                        </TooltipProvider>
                     </div>
                  </div>
               ))}
            </div>
         </div>
      );
   };

   const processFiles = (files) => {
      if (!files) return [];
      return Array.isArray(files) ? files : [files];
   };

   return (
      <>
         <DialogDescription className="sr-only">
            Private Land Timber Permit Details
         </DialogDescription>

         {missingDocuments.length > 0 ? (
            <Alert variant="destructive" className="mb-4 dark:bg-red-900/20 dark:border-red-900 dark:text-red-200">
               <AlertCircle className="h-4 w-4 dark:text-red-200" />
               <AlertTitle className="dark:text-red-200">Missing Required Documents</AlertTitle>
               <AlertDescription className="dark:text-red-200/90">
                  The following required documents are missing:
                  <ul className="list-disc list-inside mt-2">
                     {missingDocuments.map(([key, label]) => (
                        <li key={key}>{label}</li>
                     ))}
                  </ul>
               </AlertDescription>
            </Alert>
         ) : (
            <Alert variant="success" className="mb-4 bg-green-50 dark:bg-green-900/20 border-green-500 dark:border-green-900">
               <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
               <AlertTitle className="text-green-600 dark:text-green-400">Documentation Complete</AlertTitle>
               <AlertDescription className="text-green-600 dark:text-green-400/90">
                  All required documents have been submitted.
               </AlertDescription>
            </Alert>
         )}

         <div className="grid grid-cols-2 gap-4">
            <div>
               <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Name</h3>
               <p className="dark:text-gray-200">{data.name || 'N/A'}</p>
            </div>
            <div>
               <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Address</h3>
               <p className="dark:text-gray-200">{data.address || 'N/A'}</p>
            </div>
            <div>
               <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Contact Number</h3>
               <p className="dark:text-gray-200">{data.contactNumber || 'N/A'}</p>
            </div>
            <div>
               <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Request Type</h3>
               <p className="dark:text-gray-200">{data.requestType || 'N/A'}</p>
            </div>
         </div>

         <Separator className="my-4 dark:bg-gray-700" />

         <div className="space-y-4">
            <h3 className="font-semibold dark:text-gray-200">Project Details</h3>
            <div>
               <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Purpose</h3>
               <p className="whitespace-pre-wrap dark:text-gray-200">{data.purpose || 'N/A'}</p>
            </div>
         </div>

         <div className="mt-4">
            <h3 className="font-semibold dark:text-gray-200">Additional Information</h3>
            <div className="grid grid-cols-2 gap-4 mt-2">
               <div>
                  <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Tree Type</h3>
                  <p className="dark:text-gray-200">{data.treeType?.join(', ') || 'N/A'}</p>
               </div>
               <div>
                  <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Tree Status</h3>
                  <p className="dark:text-gray-200">{data.treeStatus?.join(', ') || 'N/A'}</p>
               </div>
               <div>
                  <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Land Type</h3>
                  <p className="dark:text-gray-200">{data.landType?.join(', ') || 'N/A'}</p>
               </div>
               <div>
                  <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Posing Danger</h3>
                  <p className="dark:text-gray-200">{getBooleanDisplay(data.posingDanger) || 'N/A'}</p>
               </div>
               <div>
                  <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">For Personal Use</h3>
                  <p className="dark:text-gray-200">{getBooleanDisplay(data.forPersonalUse)}</p>
               </div>
               <div>
                  <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">CLOA Holder</h3>
                  <p className="dark:text-gray-200">{getBooleanDisplay(data.isCLOA)}</p>
               </div>
               <div>
                  <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Representative</h3>
                  <p className="dark:text-gray-200">{getBooleanDisplay(data.isRepresentative)}</p>
               </div>
               <div>
                  <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">School/Organization</h3>
                  <p className="dark:text-gray-200">{getBooleanDisplay(data.isSchoolOrOrganization)}</p>
               </div>
            </div>
         </div>

         {!documentsOnRight && (
            <>
               <Separator className="my-4 dark:bg-gray-700" />
               <div className="space-y-4">
                  <h3 className="font-semibold dark:text-gray-200">Supporting Documents</h3>
                  {data.files && (
                     <ScrollArea className="h-[300px] custom-scrollbar">
                        <div className="space-y-4 pr-4">
                           {renderFileSection(processFiles(data.files.letterOfIntent), "Letter of Intent")}
                           {renderFileSection(processFiles(data.files.lguEndorsement), "LGU Endorsement")}
                           {renderFileSection(processFiles(data.files.landTenurial), "Land Tenurial Instrument")}
                           {renderFileSection(processFiles(data.files.siteDevelopmentPlan), "Site Development Plan")}
                           {renderFileSection(processFiles(data.files.environmentalCompliance), "Environmental Compliance")}
                           {renderFileSection(processFiles(data.files.fpic), "FPIC")}
                           {renderFileSection(processFiles(data.files.ownerConsent), "Owner's Consent")}
                           {renderFileSection(processFiles(data.files.pambClearance), "PAMB Clearance")}
                        </div>
                     </ScrollArea>
                  )}
               </div>
            </>
         )}
      </>
   );
};

export default PLTPDetailsRenderer;
